import { useCallback, useEffect, useMemo, useRef } from 'react'

export default function PricingIframe({ pricingIframeUrl, source = '', feature = '', paymentId = null, getAuthPayload, workspace = null, style = {}, iframeStyle = {}, onLoad, forceShowAllPlans = false }) {
  const iframeRef = useRef(null)
  const handlerRef = useRef(null)
  const paymentIntentSentRef = useRef(false)

  // Immediately strip paymentId (and cancelled) from the URL on mount.
  // This MUST run before any auth payload is computed, because usePricingAuth
  // uses window.location.href for returnUrl/cancelUrl that get sent to Stripe.
  // If paymentId stays in those URLs, Stripe's cancel redirects back with it,
  // re-triggering the payment flow in an infinite loop.
  useEffect(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.has('paymentId') || url.searchParams.has('cancelled')) {
      url.searchParams.delete('paymentId')
      url.searchParams.delete('cancelled')
      window.history.replaceState(null, '', url.toString())
    }
  }, [])

  const sendAuth = useCallback((targetWindow, targetOrigin, requestId = null) => {
    if (!getAuthPayload) return false
    const payload = getAuthPayload()
    if (!payload?.token) return false

    const responseData = {
      type: 'PRICING_COMPONENT_AUTH_RESPONSE',
      requestId: requestId || `proactive-${Date.now()}`,
      workspaceId: payload.workspaceId || null,
      userId: payload.userId || null,
      token: payload.token || null,
      returnUrl: payload.returnUrl || window.location.href,
      cancelUrl: payload.cancelUrl || window.location.href,
      currentPlanPriceId: payload.currentPlanPriceId || null,
      // Optional template/project attribution (passthrough; null unless the host supplies it)
      projectId: payload.projectId || null,
      templateId: payload.templateId || null,
      templateName: payload.templateName || null,
      templateCategory: payload.templateCategory || null,
      hideCredits: Boolean(payload.hideCredits),
      workspace: payload.workspace || {},
      source,
      codebase: "dashboard",
      feature: feature || null,
    }

    try {
      targetWindow.postMessage(responseData, targetOrigin)
      return true
    } catch (err) {
      return false
    }
  }, [feature, getAuthPayload, source])

  useEffect(() => {
    if (!pricingIframeUrl) return

    const pricingUrl = new URL(pricingIframeUrl)
    const allowedOrigins = ['http://localhost:5174', 'https://localhost:5174', pricingUrl.origin]

    const handler = (event) => {
      const { data, origin, source: eventSource } = event
      if (!data?.type?.startsWith('PRICING_')) return

      const isAllowed = allowedOrigins.includes(origin) || origin.includes('localhost')
      if (!isAllowed) return

      if (data.type === 'PRICING_COMPONENT_AUTH_REQUEST') {
        sendAuth(eventSource, origin, data.requestId)
        setTimeout(() => sendAuth(eventSource, origin, data.requestId), 200)
      }

      if (data.type === 'PRICING_COMPONENT_PAYMENT_INTENT_ACK') {
        // Acknowledged
      }

      if (data.type === 'PRICING_COMPONENT_REDIRECT' && data.url) {
        window.location.href = data.url
      }
    }

    handlerRef.current = handler
    window.addEventListener('message', handler)

    window.sendAuthToPricingIframe = () => {
      if (!iframeRef.current) return
      const targetOrigin = iframeRef.current.src.split('/').slice(0, 3).join('/')
      sendAuth(iframeRef.current.contentWindow, targetOrigin)
    }

    return () => {
      window.removeEventListener('message', handlerRef.current)
      delete window.sendAuthToPricingIframe
    }
  }, [pricingIframeUrl, sendAuth])

  useEffect(() => {
    const workspaceData = workspace || (getAuthPayload?.()?.workspace)
    if (workspaceData && Object.keys(workspaceData).length > 0 && window.pricingIframe && window.sendAuthToPricingIframe) {
      setTimeout(() => window.sendAuthToPricingIframe(), 300)
    }
  }, [workspace, getAuthPayload])

  const handleLoad = () => {
    if (iframeRef.current) window.pricingIframe = iframeRef.current

    setTimeout(() => {
      if (window.sendAuthToPricingIframe) window.sendAuthToPricingIframe()

      if (paymentId && !paymentIntentSentRef.current && iframeRef.current) {
        setTimeout(() => {
          const targetOrigin = new URL(pricingIframeUrl).origin
          iframeRef.current.contentWindow.postMessage({
            type: 'PRICING_COMPONENT_PAYMENT_INTENT',
            paymentId,
            requestId: `pi-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
          }, targetOrigin)
          paymentIntentSentRef.current = true
        }, 1000)
      }
    }, 500)

    onLoad?.()
  }

  // When the caller wants to bypass the iframe's "minimal" / simplified
  // view (e.g. "Compare plans" CTA), tack ?showAllPlans=1 onto the src.
  // The pricing-components App.jsx reads this on mount and initialises
  // its local `showAllPlans` state to true so PlanList/LifetimeDeals
  // render instead of SimplifiedPricing.
  const resolvedIframeSrc = useMemo(() => {
    if (!pricingIframeUrl) return pricingIframeUrl
    if (!forceShowAllPlans) return pricingIframeUrl
    try {
      const u = new URL(pricingIframeUrl)
      u.searchParams.set('showAllPlans', '1')
      return u.toString()
    } catch {
      // pricingIframeUrl wasn't a parseable absolute URL — fall back to a
      // best-effort append. (Shouldn't happen in practice; the iframe URL
      // comes from VITE_PRICING_IFRAME_URL which is always absolute.)
      const sep = pricingIframeUrl.includes('?') ? '&' : '?'
      return `${pricingIframeUrl}${sep}showAllPlans=1`
    }
  }, [pricingIframeUrl, forceShowAllPlans])

  if (!pricingIframeUrl) {
    return <div style={{ width: '100%', height: '100%', ...style }}><div style={{ padding: 20, textAlign: 'center' }}>Error: Pricing URL not configured</div></div>
  }

  return (
    <div style={{ width: '100%', height: '100%', border: 'none', overflow: 'hidden', ...style }}>
      <iframe ref={iframeRef} src={resolvedIframeSrc} style={{ width: '100%', height: '100%', border: 'none', display: 'block', ...iframeStyle }} title="Pricing Page" onLoad={handleLoad} />
    </div>
  )
}
